import { supabase, DreamTeamWithPlayers, TeamPlayer, TeamSubstitute, isSupabaseConfigured } from '../lib/supabase'
import { Team, Player, Formation } from '../types'

export class SupabaseTeamService {
  /**
   * Check if Supabase is properly configured
   */
  isConfigured(): boolean {
    return isSupabaseConfigured()
  }

  /**
   * Save a complete team (create or update)
   */
  async saveTeam(team: Team, userId: string): Promise<string> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured')
    }

    try {
      // Save the main team record
      const teamId = await this.saveMainTeam(team, userId)

      // Save players
      const teamPlayers = await this.saveTeamPlayers(teamId, team.players)

      // Save substitutes
      const teamSubstitutes = await this.saveTeamSubstitutes(teamId, team.substitutes)

      console.log('Team saved successfully:', {
        teamId,
        playersCount: teamPlayers.length,
        substitutesCount: teamSubstitutes.length
      })

      return teamId
    } catch (error) {
      console.error('Error saving team:', error)
      throw error
    }
  }

  /**
   * Get all teams for a user
   */
  async getUserTeams(userId: string): Promise<DreamTeamWithPlayers[]> {
    if (!this.isConfigured()) {
      return []
    }

    const { data, error } = await supabase
      .from('dream_teams')
      .select(`
        *,
        team_players(*),
        team_substitutes(*),
        team_coaches(*)
      `)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching user teams:', error)
      return []
    }

    return data || []
  }

  /**
   * Get a specific team by ID
   */
  async getTeam(teamId: string, userId: string): Promise<DreamTeamWithPlayers | null> {
    if (!this.isConfigured()) {
      return null
    }

    const { data, error } = await supabase
      .from('dream_teams')
      .select(`
        *,
        team_players(*),
        team_substitutes(*),
        team_coaches(*)
      `)
      .eq('id', teamId)
      .eq('user_id', userId)
      .single()

    if (error) {
      console.error('Error fetching team:', error)
      return null
    }

    return data
  }

  /**
   * Delete a team
   */
  async deleteTeam(teamId: string, userId: string): Promise<boolean> {
    if (!this.isConfigured()) {
      return false
    }

    const { error } = await supabase
      .from('dream_teams')
      .delete()
      .eq('id', teamId)
      .eq('user_id', userId)

    if (error) {
      console.error('Error deleting team:', error)
      return false
    }

    return true
  }

  /**
   * Convert Supabase team format to app Team format
   */
  convertToAppTeam(supabaseTeam: DreamTeamWithPlayers, formations: Formation[]): Team {
    console.log('🔄 Starting convertToAppTeam with:', {
      teamId: supabaseTeam.id,
      teamName: supabaseTeam.name,
      playersCount: supabaseTeam.team_players?.length,
      substitutesCount: supabaseTeam.team_substitutes?.length,
      substitutesRaw: supabaseTeam.team_substitutes
    });

    // Find the formation
    const formation = formations.find(f => f.id === supabaseTeam.formation_id) || formations[0]

    // Convert players
    const players: { [key: string]: Player } = {}
    supabaseTeam.team_players.forEach(player => {
      players[player.position] = {
        id: player.player_id,
        name: player.player_name,
        position: player.position as any,
        nationality: player.api_data?.nationality || 'Unknown',
        team: player.api_data?.team || 'Unknown',
        jerseyNumber: player.jersey_number,
        imageUrl: player.image_url,
        isSubstitute: false
      }
    })

    // Convert substitutes
    const substitutes: Player[] = supabaseTeam.team_substitutes.map(sub => {
      console.log('🔄 Converting substitute:', {
        id: sub.player_id,
        name: sub.player_name,
        position: sub.position
      });
      
      return {
        id: sub.player_id,
        name: sub.player_name,
        position: sub.position as any,
        nationality: sub.api_data?.nationality || 'Unknown',
        team: sub.api_data?.team || 'Unknown',
        jerseyNumber: sub.jersey_number,
        imageUrl: sub.image_url,
        isSubstitute: true
      };
    });

    console.log('✅ Conversion complete:', {
      playersCount: Object.keys(players).length,
      substitutesCount: substitutes.length,
      finalSubstitutes: substitutes
    });

    return {
      id: supabaseTeam.id,
      name: supabaseTeam.name,
      formation,
      players,
      substitutes
    }
  }

  /**
   * Migrate team data from localStorage to Supabase
   */
  async migrateFromLocalStorage(userId: string): Promise<boolean> {
    try {
      const localTeamData = localStorage.getItem('dream11-team-state')
      if (!localTeamData) {
        return false
      }

      // Check if migration already completed
      const migrationKey = `migration-completed-${userId}`
      if (localStorage.getItem(migrationKey)) {
        return true
      }

      const teamState = JSON.parse(localTeamData)
      const team = teamState.team

      if (team && team.name) {
        const savedTeam = await this.saveTeam(team, userId)
        if (savedTeam) {
          localStorage.setItem(migrationKey, 'true')
          console.log('Successfully migrated team data to Supabase')
          return true
        }
      }

      return false
    } catch (error) {
      console.error('Error migrating team from localStorage:', error)
      return false
    }
  }

  /**
   * Private helper methods
   */

  private async saveMainTeam(team: Team, userId: string): Promise<string> {
    // For existing teams, use the existing ID; for new teams, let Supabase generate UUID
    const teamData: any = {
      user_id: userId,
      name: team.name || 'My Dream Team', // Ensure name is never empty
      formation_id: team.formation.id,
      formation_name: team.formation.name,
      is_default: false,
      updated_at: new Date().toISOString()
    }

    // Only include ID if the team already has one (for updates)
    if (team.id) {
      teamData.id = team.id
    }
    
    // First, save the main team record
    const { data: savedTeam, error: teamError } = await supabase
      .from('dream_teams')
      .upsert([teamData])
      .select()
      .single()

    if (teamError) {
      console.error('Error saving team:', teamError)
      throw new Error(`Failed to save team: ${teamError.message}`)
    }

    if (!savedTeam) {
      throw new Error('No team data returned from save operation')
    }

    return savedTeam.id
  }

  private async saveTeamPlayers(teamId: string, players: { [key: string]: Player | null }): Promise<TeamPlayer[]> {
    const playerEntries = Object.entries(players)
      .filter((entry): entry is [string, Player] => entry[1] !== null) // Type guard
      .map(([position, player]) => ({
        team_id: teamId,
        player_id: player.id,
        player_name: player.name,
        position: position,
        jersey_number: player.jerseyNumber,
        image_url: player.imageUrl,
        api_data: {
          nationality: player.nationality || player.country,
          team: player.team,
          position: player.position
        }
      }))

    if (playerEntries.length === 0) {
      return []
    }

    const { data, error } = await supabase
      .from('team_players')
      .insert(playerEntries)
      .select()

    if (error) {
      console.error('Error saving team players:', error)
      return []
    }

    return data || []
  }

  private async saveTeamSubstitutes(teamId: string, substitutes: Player[]): Promise<TeamSubstitute[]> {
    if (substitutes.length === 0) {
      return []
    }

    const substituteEntries = substitutes.map(player => ({
      team_id: teamId,
      player_id: player.id,
      player_name: player.name,
      position: player.position,
      jersey_number: player.jerseyNumber,
      image_url: player.imageUrl,
      api_data: {
        nationality: player.nationality,
        team: player.team,
        position: player.position
      }
    }))

    const { data, error } = await supabase
      .from('team_substitutes')
      .insert(substituteEntries)
      .select()

    if (error) {
      console.error('Error saving team substitutes:', error)
      return []
    }

    return data || []
  }

  /**
   * Fallback to localStorage when Supabase is not available
   */
  async saveTeamFallback(team: Team): Promise<boolean> {
    try {
      const currentState = localStorage.getItem('dream11-team-state')
      const state = currentState ? JSON.parse(currentState) : { team: null, notifications: [] }
      
      state.team = team
      localStorage.setItem('dream11-team-state', JSON.stringify(state))
      
      return true
    } catch (error) {
      console.error('Error saving team to localStorage fallback:', error)
      return false
    }
  }

  async getTeamFallback(): Promise<Team | null> {
    try {
      const savedState = localStorage.getItem('dream11-team-state')
      if (savedState) {
        const state = JSON.parse(savedState)
        return state.team || null
      }
      return null
    } catch (error) {
      console.error('Error loading team from localStorage fallback:', error)
      return null
    }
  }
}

// Export singleton instance
export const supabaseTeamService = new SupabaseTeamService() 